/**
 * 편성 구성 규칙.
 *
 * **영상도 다른 콘텐츠와 함께 담는다**(2026-10-07 사용자 결정). 영상은 노출 시간과 상관없이 끝까지 재생한
 * 뒤 다음으로 넘어간다 — 그 규칙은 화면(core `videoWaitSec`)과 서버(`videoDwellSec`)가 지킨다.
 *
 * 예전에는 영상을 단독으로만 담게 했다. 노출 시간이 영상의 상한이라 이미지와 섞으면 영상이 8초에 잘렸기
 * 때문이다. 화면이 영상을 끝까지 틀게 되면서 그 제약을 걷었다.
 *
 * 규칙을 화면마다 따로 적으면 반드시 어긋나므로(바로 보내기와 플레이리스트 편집이 다르게 동작하는 순간
 * 현장에서는 "될 때도 있고 안 될 때도 있는" 기능이 된다) 여기 한 곳에 두고 양쪽이 가져다 쓴다.
 */

/** 규칙 판단에 필요한 최소 정보. 자산이든 편성 항목이든 이것만 있으면 된다. */
export interface ContentKind {
  type: 'image' | 'video' | 'web' | 'template' | 'html';
}

export function isVideo(item: ContentKind): boolean {
  return item.type === 'video';
}

/** 모두 영상인가 — 그러면 노출 시간이 쓰일 곳이 없다(영상은 끝까지 재생한다). */
export function allVideo(items: readonly ContentKind[]): boolean {
  return items.length > 0 && items.every(isVideo);
}

/**
 * 담긴 것은 빼고, 없는 것은 뒤에 담는다 — **체크 한 번이 곧 편성의 상태**다.
 *
 * 플레이리스트 편집기의 콘텐츠 담기가 쓴다. 예전에는 고르기와 담기가 두 단계라
 * "체크했는데 편성에는 없는" 중간 상태가 있었고, 패널을 닫으면 고른 것이 통째로 사라졌다.
 *
 * **뺄 때는 그 콘텐츠를 전부 뺀다.** 같은 콘텐츠가 두 줄 들어 있을 때 한 줄만 빼면 체크는
 * 그대로 남아, 푼 사람에게는 아무 일도 일어나지 않은 것으로 보인다.
 */
export function toggleContent<T extends { asset_id: string }>(current: readonly T[], candidate: T): T[] {
  if (current.some((item) => item.asset_id === candidate.asset_id)) {
    return current.filter((item) => item.asset_id !== candidate.asset_id);
  }
  return [...current, candidate];
}

/**
 * 항목 순서 바꾸기.
 *
 * 끌어놓기와 ↑↓ 버튼이 **같은 함수**를 쓴다. 각자 배열을 다루면 한쪽만 고치는 사고가
 * 나고, 편성은 순서가 곧 내용이라 그 어긋남이 화면에 그대로 나간다.
 *
 * 범위를 벗어나면 원본을 그대로 돌려준다 — 첫 항목에서 ↑ 를 눌러도 아무 일이
 * 없어야지, 조용히 끝으로 돌아가면 안 된다.
 */
export function reorderItems<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to) return [...items];
  if (from < 0 || from >= items.length) return [...items];
  if (to < 0 || to >= items.length) return [...items];

  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return next;
}
